/**
 * NDG Testimonials — view.js
 *
 * Initialise Embla Carousel (via le module partagé, cf. films-slider/riders)
 * puis câble la lecture vidéo de chaque slide :
 *   - Desktop (hover) : lecture au survol, pause (sans reset) à la sortie —
 *     on ne touche jamais à currentTime/muted : l'utilisateur peut reprendre
 *     où il en était (ou le son qu'il a choisi via les contrôles natifs) au
 *     survol suivant, plutôt que de tout perdre à chaque mouseleave.
 *   - Tactile (pas de hover) : lecture en muet dès que le slide est ≥80%
 *     visible à l'écran (IntersectionObserver), pause en sortant du cadre
 *   - Contrôles natifs : révélés au survol sur desktop (ajout/retrait de
 *     l'attribut `controls`), toujours visibles sur tactile (pas de hover
 *     pour les révéler autrement) — gérés par le navigateur, pas de bouton
 *     custom à synchroniser. La légende (auteur/ville) glisse hors champ au
 *     survol en pur CSS (:hover), voir style.scss.
 */

import { store, getElement } from '@wordpress/interactivity';
import { initSlider as initEmblaSlider } from '../../js/utils/slider.js';

store( 'ndg/testimonials', {
	callbacks: {
		initSlider() {
			const { ref } = getElement();
			if ( ! ref ) return;

			initEmblaSlider( ref, {
				wrapperSelector: '.ndg-testi',
				labelSelector:   '.ndg-testi__title',
				fallbackLabel:   'Testimonials',
				watchResize:     true,
			} );

			const canHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;
			const cards    = ref.querySelectorAll( '.ndg-testi__card' );

			if ( canHover ) {
				cards.forEach( ( card ) => {
					const video = card.querySelector( '.ndg-testi__video' );
					if ( ! video ) return;

					card.addEventListener( 'mouseenter', () => {
						if ( ref.classList.contains( 'is-dragging' ) ) return;
						video.controls = true;
						video.play().catch( () => {} );
					} );

					// Pause seulement : pas de reset currentTime/muted, pour
					// ne pas perdre la progression ni le son choisi par
					// l'utilisateur via les contrôles natifs.
					card.addEventListener( 'mouseleave', () => {
						video.pause();
						video.controls = false;
					} );
				} );
			} else {
				// Tactile : pas de hover pour révéler les contrôles, donc
				// visibles en permanence (l'utilisateur en a besoin pour
				// mettre en pause/couper le son manuellement). La légende
				// reste visible en continu ici (le CSS :hover ne s'applique
				// pas sur tactile) — léger chevauchement accepté.
				cards.forEach( ( card ) => {
					const video = card.querySelector( '.ndg-testi__video' );
					if ( video ) video.controls = true;
				} );

				const observer = new IntersectionObserver( ( entries ) => {
					entries.forEach( ( entry ) => {
						const video = entry.target.querySelector( '.ndg-testi__video' );
						if ( ! video ) return;
						if ( entry.isIntersecting ) {
							video.play().catch( () => {} );
						} else {
							video.pause();
						}
					} );
				}, { threshold: 0.8 } );

				cards.forEach( ( card ) => observer.observe( card ) );
			}
		},
	},
} );
